import { AUTH0_CLIENT_ID, AUTH0_DOMAIN, DOMAIN, USE_FEDERATED_AUTH } from 'config';
import { LoginCredentials, Login, LoginContext, LoginContextApi } from '../types';
import { saveUserData, saveToken, deleteUserData, getUserData } from './storage';
import { clearTableStateStorage } from 'lib/storage/clearTableStateStorage';

const userDataTransform = (user: LoginContextApi): LoginContext => {
  if (!user) {
    return user;
  }
  return {
    id: user.Id,
    name: user.Name,
    organizationType: user.OrganizationTypeId,
    organizationName: user.Organization.Name,
    email: user.Email,
    roleNames: user.RoleNames,
    designerOrganizationName: user.Organization.Name,
    designerOrganizationId: user.Organization.Id,
    brands: user.Brands,
    organizationId: user.OrganizationId,
    signUpDate: user.SignUpDate?.replace(/[^0-9.]/g, '') || '',
    planType: user.PlanType,
    planTier: user.PlanTier,
    platformNewsletterSubscription: user.PlatformNewsletterSubscription,
    inviteCode: user.Organization.InviteCode,
    organizationInfo: {
      address: user?.Organization?.Address,
      zipCode: user?.Organization?.ZipCode,
      city: user?.Organization?.City,
      stateOrRegion: user?.Organization?.StateOrRegion,
      website: user?.Organization?.WebSite,
      phoneNumber: user?.Organization?.PhoneNumber,
      faxNumber: user?.Organization?.FaxNumber,
      vatNumber: user?.Organization?.VATNumber,
      localCurrency: user?.Organization?.LocalCurrency,
      country: user?.Organization?.Country
        ? {
            id: user.Organization.Country.Id,
            name: user.Organization.Country.Name,
            code: user.Organization.Country.Code,
          }
        : undefined,
    },
  };
};

export const userFn = async (): Promise<Login> => {
  const user = getUserData();
  if (USE_FEDERATED_AUTH) {
    return {};
  }
  const response = await fetch(`${DOMAIN}/LoggedInUser`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    return null;
  }
  const data = await response.json();
  const loginContext = userDataTransform(data);
  saveToken(loginContext);
  return {
    user: user.userData || null,
    loginContext,
  };
};

export const loginFn = async (user: LoginCredentials) => {
  const response = await fetch(`${DOMAIN}/betalogin`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(user),
  });
  if (!response.ok) {
    const result = await response.json();
    // Extract error message from the response
    throw new Error(result.Message || `Error: ${response.status} ${response.statusText}`);
  }
  const data = await response.json();
  if (data.Message && data.Status === 'error') {
    throw new Error(data.Message || `Error: ${data.Message} ${data.Status}`);
  }
  saveUserData(data);
  if (!data.isMultiselect) {
    const tokenLogin = await tokenFn({
      ...user,
      loginContext: data.loginContexts[0],
      user: data,
    });
    return tokenLogin;
  }
  const login: Login = {
    user: data,
    token: null,
    ...user,
  };
  return login;
};

export const logoutFn = async () => {
  deleteUserData();
  clearTableStateStorage();
  // DL-61835 (R5/X3): in federated mode this endpoint returns the Auth0 /v2/logout URL instead
  // of redirecting itself (it's a JSON endpoint) — without navigating there, the Auth0 SSO
  // session (store A) survives and silently re-authenticates the app on the next reload, which
  // is exactly what the DL-61312 60s loop-breaker was papering over. The caller (the api
  // instance's 401 monitor) skips its own reset-to-'/' whenever USE_FEDERATED_AUTH is on, on the
  // assumption that this function always navigates somewhere — so the fetch itself (not just
  // response.json()) must be inside the try/catch: a rejected request must still navigate
  // somewhere rather than throwing out of logoutFn and leaving the 401'd page loaded.
  //
  // That fallback can't just be '/': this module isn't a React hook (it backs the plain `api`
  // instance's 401 monitor, not a component), so it has no `useAuth0()` to call the SDK's own
  // logout() the way `federatedAuthHooks.ts`'s useLogout does for the same failure case. Falling
  // back to '/' would reload the SPA with the Auth0 SSO cookie still alive, so the very next
  // silent prompt=none round trip signs the same user straight back in (Greptile catch on this
  // PR). Build the Auth0 RP-initiated /v2/logout URL directly from config instead, so a failed/
  // malformed /betalogin/logout response still terminates store A.
  if (USE_FEDERATED_AUTH) {
    let logoutUrl = `https://${AUTH0_DOMAIN}/v2/logout?client_id=${AUTH0_CLIENT_ID}&returnTo=${encodeURIComponent(
      window.location.origin
    )}`;
    try {
      const response = await fetch(`${DOMAIN}/betalogin/logout`, {
        method: 'GET',
        credentials: 'include',
      });
      const data = await response.json();
      if (data?.LogoutUrl) {
        logoutUrl = data.LogoutUrl;
      }
    } catch {
      // Request failed, or wasn't the federated JSON shape — fall back to the Auth0 logout URL above.
    }
    window.location.assign(logoutUrl);
  } else {
    await fetch(`${DOMAIN}/betalogin/logout`, {
      method: 'GET',
      credentials: 'include',
    });
  }
  return;
};

// eslint-disable-next-line require-await
export const registerFn = async () => {
  return null;
};

export const tokenFn = async (user: LoginCredentials) => {
  const id = user.loginContext?.id || 0;
  const orgId =
    user.loginContext?.organizationType === 1 ? 0 : user.loginContext?.designerOrganizationId;
  const response = await fetch(
    `${DOMAIN}/betalogin/select/${id}?designerorgid=${orgId}&rememberme=${user.RememberMe}`,
    {
      method: 'GET',
      credentials: 'include',
    }
  );
  if (!response.ok) {
    throw new Error('Error logging in.');
  }
  const data = await response.json();
  saveToken(user.loginContext);
  const login: Login = {
    user: user.user,
    token: data,
    loginContext: user.loginContext,
    Username: user.Username,
    Password: user.Password,
  };
  return login;
};
